import { useState, useCallback, useRef, useEffect } from 'react';
import apiService from '../services/api';

/**
 * 下载任务状态枚举
 */
export const DOWNLOAD_STATUS = {
  PENDING: 'pending',
  DOWNLOADING: 'downloading',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled'
};

/**
 * 下载相关的自定义Hook
 */
export const useDownload = () => {
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);
  const userTaskIds = useRef(new Set()); // 跟踪用户创建的任务ID
  const completedTaskIds = useRef(new Set()); // 跟踪已完成的任务ID，不再轮询
  const activeTaskIds = useRef(new Set()); // 跟踪需要轮询的活跃任务ID
  const taskCreationTimes = useRef(new Map()); // 记录任务创建时间
  const task404Times = useRef(new Map()); // 记录任务首次404的时间
  
  // 从localStorage加载用户任务ID
  useEffect(() => {
    try {
      const stored = localStorage.getItem('streammate_user_tasks');
      if (stored) {
        const taskIds = JSON.parse(stored);
        userTaskIds.current = new Set(taskIds);
        console.log('从localStorage加载任务ID:', taskIds.length);
      }
    } catch (error) {
      console.error('加载用户任务ID失败:', error);
    }
  }, []);
  
  // 保存用户任务ID到localStorage
  const saveUserTaskIds = useCallback(() => {
    try {
      const taskIds = Array.from(userTaskIds.current);
      localStorage.setItem('streammate_user_tasks', JSON.stringify(taskIds));
    } catch (error) {
      console.error('保存用户任务ID失败:', error);
    }
  }, []);

  /**
   * 开始轮询任务状态
   */
  const startPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    intervalRef.current = setInterval(async () => {
      try {
        // 只查询用户创建的且未完成的任务
        const allTaskIds = Array.from(userTaskIds.current);
        const activeTaskIds = allTaskIds.filter(taskId => {
          // 如果任务在完成列表中，检查是否在404容错期内
          if (completedTaskIds.current.has(taskId)) {
            const taskCreationTime = taskCreationTimes.current.get(taskId);
            const first404Time = task404Times.current.get(taskId);
            const now = new Date();
            
            // 如果是新创建的任务（10分钟内）且有404记录，允许重新尝试
            if (taskCreationTime && first404Time) {
              const timeSinceCreation = now - taskCreationTime;
              const timeSince404 = now - first404Time;
              
              if (timeSinceCreation < 10 * 60 * 1000 && timeSince404 < 10 * 60 * 1000) {
                console.log(`任务 ${taskId} 在404容错期内，重新尝试轮询`);
                completedTaskIds.current.delete(taskId); // 从完成列表中移除，允许重新轮询
                return true;
              }
            }
            return false;
          }
          return true;
        });
        
        if (activeTaskIds.length === 0) {
          return; // 没有活跃任务需要查询
        }
        
        console.log('轮询活跃任务状态，任务数量:', activeTaskIds.length);
        
        // 并发查询所有活跃任务
        const taskPromises = activeTaskIds.map(async (taskId) => {
          try {
            const result = await apiService.getTask(taskId);
            if (result.success) {
              const taskData = {
                id: taskId,
                ...result.data,
                progress: result.data.progress || 0,
                status: result.data.status || DOWNLOAD_STATUS.PENDING
              };
              
              // 任务查询成功，清除404记录
              if (task404Times.current.has(taskId)) {
                task404Times.current.delete(taskId);
                console.log(`任务 ${taskId} 查询成功，清除404记录`);
              }
              
              // 检查任务是否已完成
              const isCompleted = taskData.progress >= 100 || 
                                taskData.status === DOWNLOAD_STATUS.COMPLETED ||
                                taskData.status === DOWNLOAD_STATUS.FAILED ||
                                taskData.status === DOWNLOAD_STATUS.CANCELLED;
              
              if (isCompleted) {
                // 将已完成的任务添加到已完成列表，停止轮询
                completedTaskIds.current.add(taskId);
                console.log(`任务 ${taskId} 已完成，停止轮询。状态: ${taskData.status}, 进度: ${taskData.progress}%`);
                
                // 如果任务失败或被取消，从localStorage中移除任务ID
                if (taskData.status === DOWNLOAD_STATUS.FAILED || taskData.status === DOWNLOAD_STATUS.CANCELLED) {
                  userTaskIds.current.delete(taskId);
                  activeTaskIds.current.delete(taskId);
                  console.log(`任务 ${taskId} 失败或被取消，已从存储中移除`);
                  // 异步保存到localStorage，避免阻塞UI
                  setTimeout(() => saveUserTaskIds(), 0);
                }
              }
              
              return taskData;
            } else {
              // API返回失败，可能是404或其他错误
              console.log(`任务 ${taskId} API返回失败: ${result.error}`);
              
              // 检查是否是404错误或任务不存在错误
              const is404OrNotFound = result.isNotFound || 
                                     result.statusCode === 404 ||
                                     (result.error && (
                                       result.error.includes('404') || 
                                       result.error.includes('Not Found') || 
                                       result.error.includes('not found') ||
                                       result.error.includes('does not exist') ||
                                       result.error.includes('不存在')
                                     ));
              
              if (is404OrNotFound) {
                const now = new Date();
                const taskCreationTime = taskCreationTimes.current.get(taskId);
                const timeSinceCreation = taskCreationTime ? now - taskCreationTime : Infinity;
                
                // 如果任务创建时间少于10分钟，不立即删除（增加容错时间）
                if (timeSinceCreation < 10 * 60 * 1000) {
                  console.log(`任务 ${taskId} 返回404，但创建时间不足10分钟(${Math.round(timeSinceCreation/1000)}秒)，暂不删除`);
                  
                  // 对于非常新的任务（2分钟内），不暂停轮询，继续尝试
                  if (timeSinceCreation < 2 * 60 * 1000) {
                    console.log(`任务 ${taskId} 创建不足2分钟，继续轮询等待任务初始化`);
                    return null; // 继续轮询，不暂停
                  }
                  
                  completedTaskIds.current.add(taskId); // 暂停轮询但不删除
                  return null;
                }
                
                // 记录首次404时间
                if (!task404Times.current.has(taskId)) {
                  task404Times.current.set(taskId, now);
                  console.log(`任务 ${taskId} 首次返回404，开始计时`);
                  completedTaskIds.current.add(taskId); // 暂停轮询但不删除
                  return null;
                }
                
                // 检查404持续时间
                const first404Time = task404Times.current.get(taskId);
                const timeSince404 = now - first404Time;
                
                if (timeSince404 >= 10 * 60 * 1000) { // 404持续10分钟才删除（增加时间）
                  // 404超过10分钟，确实不存在，从存储中移除
                  userTaskIds.current.delete(taskId);
                  activeTaskIds.current.delete(taskId);
                  completedTaskIds.current.add(taskId);
                  taskCreationTimes.current.delete(taskId);
                  task404Times.current.delete(taskId);
                  console.log(`任务 ${taskId} 404持续${Math.round(timeSince404/1000)}秒，已从存储中移除`);
                  // 异步保存到localStorage，避免阻塞UI
                  setTimeout(() => saveUserTaskIds(), 0);
                } else {
                  console.log(`任务 ${taskId} 404持续${Math.round(timeSince404/1000)}秒，等待满10分钟再删除`);
                  completedTaskIds.current.add(taskId); // 暂停轮询但不删除
                }
              } else {
                // 其他错误，标记为已完成避免持续查询，但保留在存储中
                completedTaskIds.current.add(taskId);
                console.log(`任务 ${taskId} 查询失败，暂停轮询但保留在存储中`);
              }
              
              return null;
            }
          } catch (error) {
            console.error(`查询任务 ${taskId} 失败:`, error);
            
            // 检查HTTP状态码
            const isNotFound = error.response?.status === 404 || 
                              error.message?.includes('404') ||
                              error.message?.includes('Not Found');
            
            if (isNotFound) {
              const now = new Date();
              const taskCreationTime = taskCreationTimes.current.get(taskId);
              const timeSinceCreation = taskCreationTime ? now - taskCreationTime : Infinity;
              
              // 如果任务创建时间少于10分钟，不立即删除（增加容错时间）
              if (timeSinceCreation < 10 * 60 * 1000) {
                console.log(`任务 ${taskId} 网络404错误，但创建时间不足10分钟(${Math.round(timeSinceCreation/1000)}秒)，暂不删除`);
                completedTaskIds.current.add(taskId);
                return null;
              }
              
              // 记录首次404时间
              if (!task404Times.current.has(taskId)) {
                task404Times.current.set(taskId, now);
                console.log(`任务 ${taskId} 首次网络404错误，开始计时`);
                completedTaskIds.current.add(taskId);
                return null;
              }
              
              // 检查404持续时间
              const first404Time = task404Times.current.get(taskId);
              const timeSince404 = now - first404Time;
              
              if (timeSince404 >= 10 * 60 * 1000) {
                // 404超过10分钟，确实不存在，从存储中移除
                userTaskIds.current.delete(taskId);
                activeTaskIds.current.delete(taskId);
                taskCreationTimes.current.delete(taskId);
                task404Times.current.delete(taskId);
                console.log(`任务 ${taskId} 网络404持续${Math.round(timeSince404/1000)}秒，已从存储中移除`);
                // 异步保存到localStorage，避免阻塞UI
                setTimeout(() => saveUserTaskIds(), 0);
              } else {
                console.log(`任务 ${taskId} 网络404持续${Math.round(timeSince404/1000)}秒，等待满10分钟再删除`);
                completedTaskIds.current.add(taskId);
              }
            } else {
              // 其他网络错误，标记为已完成避免持续查询，但保留在存储中
              console.log(`任务 ${taskId} 网络错误，暂停轮询但保留在存储中`);
            }
            
            // 查询失败的任务也标记为已完成，避免持续失败
            completedTaskIds.current.add(taskId);
            return null;
          }
        });
        
        const taskResults = await Promise.all(taskPromises);
        const validTasks = taskResults.filter(task => task !== null);
        
        if (validTasks.length > 0) {
          setTasks(prev => {
            // 更新现有任务或添加新任务
            const updatedTasks = [...prev];
            
            validTasks.forEach(newTask => {
              const existingIndex = updatedTasks.findIndex(task => task.id === newTask.id);
              if (existingIndex >= 0) {
                // 更新现有任务
                updatedTasks[existingIndex] = {
                  ...updatedTasks[existingIndex],
                  ...newTask,
                  createdAt: updatedTasks[existingIndex].createdAt // 保持原创建时间
                };
              } else {
                // 添加新任务（理论上不应该发生，因为我们只查询已知的任务ID）
                updatedTasks.unshift({
                  ...newTask,
                  createdAt: new Date()
                });
              }
            });
            
            return updatedTasks;
          });
        }
        
        // 如果没有活跃任务了，停止轮询
        if (activeTaskIds.current.size === 0) {
          console.log('所有任务已完成，停止轮询');
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } catch (error) {
        console.error('轮询任务状态失败:', error);
      }
    }, 2000); // 每2秒轮询一次
  }, []);

  /**
   * 开始下载视频
   */
  const startDownload = useCallback(async (url, format = 'best', fileExt = 'mp4', options = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      console.log('useDownload: 调用API下载视频', { url, format, fileExt, options });
      
      const result = await apiService.downloadVideo(url, fileExt, format, options);
      
      console.log('useDownload: API返回结果', result);
      
      if (result.success) {
        // 添加新任务到列表
        const taskId = result.data.task_id || Date.now().toString();
        
        // 从file_path提取文件名
        let filename = '未知文件';
        if (result.data.file_path) {
          const pathParts = result.data.file_path.split('/');
          filename = pathParts[pathParts.length - 1];
        }
        
        // 处理创建时间
        let createdAt = new Date();
        if (result.data.created_at) {
          // 将created_at的整数部分转换为当前时区的时间
          const timestamp = Math.floor(result.data.created_at);
          createdAt = new Date(timestamp * 1000); // 转换为毫秒
        }
        
        const newTask = {
          id: taskId,
          url,
          format,
          fileExt,
          status: DOWNLOAD_STATUS.PENDING,
          progress: 0,
          filename: filename,
          createdAt: createdAt,
          ...result.data
        };
        
        console.log('useDownload: 创建新任务', newTask);
        
        // 记录用户创建的任务ID
        userTaskIds.current.add(taskId);
        saveUserTaskIds(); // 保存到localStorage
        
        // 记录任务创建时间
        taskCreationTimes.current.set(taskId, new Date());
        
        // 添加到活跃任务列表（需要轮询的任务）
        activeTaskIds.current.add(taskId);
        console.log(`任务 ${taskId} 添加到活跃轮询列表`);
        
        setTasks(prev => [newTask, ...prev]);
        
        // 开始轮询任务状态
        startPolling();
        
        return { success: true, task: newTask };
      } else {
        console.error('useDownload: API返回失败', result.error);
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      console.error('useDownload: 捕获异常', err);
      const errorMsg = err.message || '下载失败';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setIsLoading(false);
    }
  }, [startPolling]);

  /**
   * 获取用户任务
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchTasks = useCallback(async () => {
    try {
      // 如果没有用户任务，直接返回
      const taskIds = Array.from(userTaskIds.current);
      if (taskIds.length === 0) {
        setTasks([]);
        return;
      }
      
      console.log('获取用户任务，任务数量:', taskIds.length);
      
      // 用于跟踪需要从存储中移除的任务ID
      const tasksToRemove = new Set();
      
      // 并发查询所有用户任务
      const taskPromises = taskIds.map(async (taskId) => {
        try {
          const result = await apiService.getTask(taskId);
          if (result.success) {
            // 从file_path提取文件名
            let filename = '未知文件';
            if (result.data.file_path) {
              const pathParts = result.data.file_path.split('/');
              filename = pathParts[pathParts.length - 1];
            }
            
            // 处理创建时间
            let createdAt = new Date();
            if (result.data.created_at) {
              // 将created_at的整数部分转换为当前时区的时间
              const timestamp = Math.floor(result.data.created_at);
              createdAt = new Date(timestamp * 1000); // 转换为毫秒
            }
            
            const taskData = {
              id: taskId,
              url: result.data.url,
              format: result.data.format,
              fileExt: result.data.file_ext,
              status: result.data.status || DOWNLOAD_STATUS.PENDING,
              progress: result.data.progress || 0,
              filename: filename,
              createdAt: createdAt,
              ...result.data
            };
            
            // 如果任务失败或被取消，标记为需要移除
            if (taskData.status === DOWNLOAD_STATUS.FAILED || taskData.status === DOWNLOAD_STATUS.CANCELLED) {
              tasksToRemove.add(taskId);
              console.log(`初始化时发现失败/取消任务 ${taskId}，将从存储中移除`);
              return null; // 不在UI中显示失败的任务
            }
            
            return taskData;
          } else {
            // API返回失败，检查是否是404错误
            const is404OrNotFound = result.isNotFound || 
                                   result.statusCode === 404 ||
                                   (result.error && (
                                     result.error.includes('404') || 
                                     result.error.includes('Not Found') || 
                                     result.error.includes('not found') ||
                                     result.error.includes('does not exist') ||
                                     result.error.includes('不存在')
                                   ));
            
            if (is404OrNotFound) {
              tasksToRemove.add(taskId);
              console.log(`初始化时发现不存在的任务 ${taskId}，将从存储中移除`);
            } else {
              console.log(`初始化时任务 ${taskId} 查询失败: ${result.error}`);
            }
            
            return null;
          }
        } catch (error) {
          console.error(`获取任务 ${taskId} 失败:`, error);
          
          // 检查是否是404错误
          const isNotFound = error.response?.status === 404 || 
                            error.message?.includes('404') ||
                            error.message?.includes('Not Found');
          
          if (isNotFound) {
            tasksToRemove.add(taskId);
            console.log(`初始化时发现不存在的任务 ${taskId} (404错误)，将从存储中移除`);
          }
          
          return null;
        }
      });
      
      const taskResults = await Promise.all(taskPromises);
      const validTasks = taskResults.filter(task => task !== null);
      
      // 从存储中移除无效的任务ID
      if (tasksToRemove.size > 0) {
        tasksToRemove.forEach(taskId => {
          userTaskIds.current.delete(taskId);
          activeTaskIds.current.delete(taskId);
          completedTaskIds.current.add(taskId);
          taskCreationTimes.current.delete(taskId);
          task404Times.current.delete(taskId);
        });
        // 保存更新后的任务列表到localStorage
        saveUserTaskIds();
        console.log(`初始化时移除了 ${tasksToRemove.size} 个无效任务`);
      }
      
      // 检查哪些任务仍然是活跃的（未完成的）
      validTasks.forEach(task => {
        const isCompleted = task.progress >= 100 || 
                          task.status === DOWNLOAD_STATUS.COMPLETED ||
                          task.status === DOWNLOAD_STATUS.FAILED ||
                          task.status === DOWNLOAD_STATUS.CANCELLED;
        
        if (!isCompleted) {
          // 添加到活跃任务列表
          activeTaskIds.current.add(task.id);
        }
      });
      
      console.log(`初始化完成，活跃任务数量: ${activeTaskIds.current.size}`);
      
      setTasks(validTasks);
    } catch (err) {
      console.error('获取任务列表失败:', err);
    }
  }, [saveUserTaskIds]);

  /**
   * 获取特定任务详情
   */
  const fetchTask = useCallback(async (taskId) => {
    try {
      const result = await apiService.getTask(taskId);
      
      if (result.success) {
        // 更新任务列表中的对应任务
        setTasks(prev => prev.map(task => 
          task.id === taskId 
            ? { ...task, ...result.data, progress: result.data.progress || task.progress }
            : task
        ));
        
        return result.data;
      }
    } catch (err) {
      console.error('获取任务详情失败:', err);
    }
    return null;
  }, []);

  /**
   * 下载完成的文件
   */
  const downloadFile = useCallback(async (taskId) => {
    try {
      const result = await apiService.downloadVideoFile(taskId);
      
      if (result.success) {
        // 创建下载链接
        const blob = new Blob([result.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // 从响应头获取文件名
        const contentDisposition = result.headers['content-disposition'];
        let filename = 'video.mp4';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }
        
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  /**
   * 停止轮询
   */
  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  /**
   * 清除错误
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * 删除任务
   */
  const removeTask = useCallback((taskId) => {
    // 从任务列表中移除
    setTasks(prev => prev.filter(task => task.id !== taskId));
    
    // 从跟踪列表中移除
    userTaskIds.current.delete(taskId);
    
    // 从活跃任务列表中移除
    activeTaskIds.current.delete(taskId);
    
    // 从已完成任务列表中移除
    completedTaskIds.current.delete(taskId);
    
    // 清理时间记录
    taskCreationTimes.current.delete(taskId);
    task404Times.current.delete(taskId);
    
    saveUserTaskIds(); // 保存到localStorage
    
    console.log('移除任务:', taskId, '剩余用户任务:', userTaskIds.current.size, '剩余活跃任务:', activeTaskIds.current.size);
  }, [saveUserTaskIds]);

  /**
   * 获取活跃任务数量（正在轮询的任务）
   */
  const getActiveTasksCount = useCallback(() => {
    return activeTaskIds.current.size;
  }, []);

  /**
   * 获取正在进行的任务数量（用于UI显示）
   */
  const getRunningTasksCount = useCallback(() => {
    return tasks.filter(task => 
      task.status === DOWNLOAD_STATUS.PENDING || 
      task.status === DOWNLOAD_STATUS.DOWNLOADING
    ).length;
  }, [tasks]);

  /**
   * 清理旧任务（自动移除超过24小时的已完成/失败任务）
   */
  const cleanupOldTasks = useCallback(() => {
    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    
    setTasks(prev => {
      const activeTasks = prev.filter(task => {
        // 立即移除失败或取消的任务
        if (task.status === DOWNLOAD_STATUS.FAILED || task.status === DOWNLOAD_STATUS.CANCELLED) {
          userTaskIds.current.delete(task.id);
          taskCreationTimes.current.delete(task.id);
          task404Times.current.delete(task.id);
          console.log(`自动清理失败/取消任务: ${task.id}`);
          return false;
        }
        
        // 保留活跃任务
        if (task.status === DOWNLOAD_STATUS.PENDING || task.status === DOWNLOAD_STATUS.DOWNLOADING) {
          return true;
        }
        
        // 保留24小时内的完成任务
        if (task.createdAt && task.createdAt > oneDayAgo && task.status === DOWNLOAD_STATUS.COMPLETED) {
          return true;
        }
        
        // 移除旧的完成任务
        userTaskIds.current.delete(task.id);
        taskCreationTimes.current.delete(task.id);
        task404Times.current.delete(task.id);
        return false;
      });
      
      // 如果有任务被清理，保存更新后的任务ID列表
      if (activeTasks.length !== prev.length) {
        saveUserTaskIds();
        console.log(`清理了 ${prev.length - activeTasks.length} 个旧任务`);
      }
      
      return activeTasks;
    });
  }, [saveUserTaskIds]);

  /**
   * 手动清理无效任务（失败、取消、不存在的任务）
   */
  const cleanupInvalidTasks = useCallback(async () => {
    const taskIds = Array.from(userTaskIds.current);
    if (taskIds.length === 0) return 0;

    let removedCount = 0;
    const tasksToRemove = new Set();

    console.log('开始手动清理无效任务...');

    // 检查所有任务
    const checkPromises = taskIds.map(async (taskId) => {
      try {
        const result = await apiService.getTask(taskId);
        
        if (!result.success) {
          // API调用失败，检查是否是404
          const is404OrNotFound = result.isNotFound || 
                                 result.statusCode === 404 ||
                                 (result.error && (
                                   result.error.includes('404') || 
                                   result.error.includes('Not Found') || 
                                   result.error.includes('not found') ||
                                   result.error.includes('does not exist') ||
                                   result.error.includes('不存在')
                                 ));
          
          if (is404OrNotFound) {
            tasksToRemove.add(taskId);
            console.log(`手动清理: 任务 ${taskId} 不存在`);
          }
        } else {
          // 任务存在，检查状态
          const status = result.data.status;
          if (status === DOWNLOAD_STATUS.FAILED || status === DOWNLOAD_STATUS.CANCELLED) {
            tasksToRemove.add(taskId);
            console.log(`手动清理: 任务 ${taskId} 状态为 ${status}`);
          }
        }
      } catch (error) {
        // 网络错误，检查是否是404
        const isNotFound = error.response?.status === 404;
        if (isNotFound) {
          tasksToRemove.add(taskId);
          console.log(`手动清理: 任务 ${taskId} 返回404错误`);
        }
      }
    });

    await Promise.all(checkPromises);

    // 移除无效任务
    if (tasksToRemove.size > 0) {
      tasksToRemove.forEach(taskId => {
        userTaskIds.current.delete(taskId);
        activeTaskIds.current.delete(taskId);
        completedTaskIds.current.add(taskId);
        taskCreationTimes.current.delete(taskId);
        task404Times.current.delete(taskId);
      });

      // 更新UI中的任务列表
      setTasks(prev => prev.filter(task => !tasksToRemove.has(task.id)));

      // 保存到localStorage
      saveUserTaskIds();
      
      removedCount = tasksToRemove.size;
      console.log(`手动清理完成，移除了 ${removedCount} 个无效任务`);
    } else {
      console.log('手动清理完成，没有发现无效任务');
    }

    return removedCount;
  }, [saveUserTaskIds]);

  /**
   * 格式化文件大小
   */
  const formatFileSize = useCallback((bytes) => {
    if (!bytes) return '未知大小';
    
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  }, []);

  /**
   * 格式化持续时间
   */
  const formatDuration = useCallback((seconds) => {
    if (!seconds) return '未知时长';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    }
  }, []);

  // 组件卸载时清理轮询
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  // 初始化时获取任务列表
  useEffect(() => {
    // 延迟一下，确保localStorage中的任务ID已经加载
    const timer = setTimeout(() => {
      fetchTasks();
      // 清理旧任务
      cleanupOldTasks();
    }, 100);
    
    return () => clearTimeout(timer);
  }, [fetchTasks, cleanupOldTasks]);

  return {
    tasks,
    isLoading,
    error,
    startDownload,
    fetchTasks,
    fetchTask,
    downloadFile,
    startPolling,
    stopPolling,
    clearError,
    removeTask,
    getActiveTasksCount,
    getRunningTasksCount,
    formatFileSize,
    formatDuration,
    cleanupOldTasks,
    cleanupInvalidTasks
  };
}; 